/**
 * @file: This file holds the media the operator can put in a playlist.
 *
 * The catalogue is every media of the library, described once. A description
 * has the name of the media, its labels, its kind and the colour of that kind.
 * It also says whether the media can play on the scenes of the playlist. A
 * digest reads those fields for every row and for every cell of the time strip.
 * They are derived here, and not on each read.
 *
 * The catalogue also gives the lists that are about media and not about an
 * order. It says which media a label chip offers. It says which media a
 * takeover can use as its bumper. It says which media the rows of a playlist
 * hold.
 *
 * The reason to change this file is a change in what a media is, or in whether
 * it can play. The order of a playlist is in `compose-state.js`.
 *
 * The class has no Angular dependency.
 */

import { rowKey, kindOf } from '../../../../shared/playlist-spread'
import { hasAssets } from '../../../../shared/media-types'
import { NEUTRAL_GREY } from 'common/colours'
import { compareNames, nameMatcher } from 'common/name-order'
import { orderLabels } from 'common/label-order'

/** A media with no kind label keeps the grey of the app. */
const NO_KIND_COLOUR = NEUTRAL_GREY
const NO_KIND_NAME = 'No kind label'
const UNKNOWN_NAME = 'Unknown media'

/** The kind label of the act bumpers in the production data. */
const BUMPER_KIND = 'Friends'

/**
 * How many media the bumper picker offers at once. The whole library is too
 * long to scroll, and the search narrows it to the act.
 */
const BUMPER_CHOICES = 40

/** The identity of a media is its type and its id together. */
const mediaRef = ({ mediaType, mediaId }) => `${mediaType}:${mediaId}`

/**
 * This function gives the recipe line a media belongs to. The duration is part
 * of the line, as it is in `rowKey`.
 */
const mediaKey = media => rowKey({
  mediaType: media.mediaType,
  mediaId: media.mediaId,
  duration: media.durationMs
})

/** This order puts the newest upload first, so a new film is at the top. */
const newestFirst = (a, b) => b.createdAtMs - a.createdAtMs

/**
 * The names the kind rule must skip, because a scene name is not a kind. An
 * empty list is a list the app has not filled yet, and is not an answer.
 */
const sceneNamesOf = scenes => (scenes || []).map(one => one.name).filter(Boolean)

/** The media references of each label, by the id of the label. */
function refsByLabel (media) {
  return media.reduce((found, one) => {
    one.labels.forEach(label => {
      const refs = found.get(label.id) || new Set()
      refs.add(mediaRef(one))
      found.set(label.id, refs)
    })

    return found
  }, new Map())
}

const hasSceneNames = scenes => sceneNamesOf(scenes).length > 0

const ORIENTATION_NAME = Object.freeze({ l: 'landscape', p: 'portrait' })

const orientationName = orientation => ORIENTATION_NAME[orientation] || 'matching'

/** The screen a note is about. A scene without a name can only be this one. */
const screenName = screen => (screen.name ? `scene ${screen.name}` : 'this scene')

/**
 * Why the server will not accept a media, as a code. The note is the text
 * beside the line. The sentence is the text over the sequence, which also says
 * what to do about it. An orientation block always names the screen it failed.
 */
const BLOCKER_NOTE = Object.freeze({
  duration: () => 'Duration not known yet',
  orientation: screen =>
    `No ${orientationName(screen.orientation)} asset for ${screenName(screen)}`
})

const BLOCKER_SENTENCE = Object.freeze({
  duration: name => `${name} has no duration yet. Remove it to save.`,
  orientation: (name, screen) =>
    `${name} has no ${orientationName(screen.orientation)} asset. Remove it to save.`
})

const NO_DURATION = Object.freeze({ blocker: 'duration', screen: null })

/**
 * This function returns the fields that say whether something can play. The
 * whole block is kept, so a row can read the screen it failed, and the two
 * derived fields are written once, because a digest reads them for every row.
 */
function blockedBy (block) {
  return {
    block: block || null,
    available: !block,
    blockerNote: block ? BLOCKER_NOTE[block.blocker](block.screen) : null
  }
}

/** Whether a media has an asset for one orientation. */
const fitsScreen = (orientations, orientation) =>
  orientations === 'both' || orientations === orientation

/**
 * The first screen a media cannot fill, as a block, or null.
 *
 * A playlist can be on more than one scene, and the scenes can differ in
 * orientation. The media then needs an asset for every one of them, because the
 * same playlist plays on all of them.
 *
 * Only a media with assets can be stopped. The player draws the other types, so
 * those fit every scene.
 */
function orientationBlock (media, screens) {
  if (!hasAssets(media.mediaType)) {
    return null
  }

  const missing = screens.find(screen => !fitsScreen(media.orientations, screen.orientation))

  return missing ? { blocker: 'orientation', screen: missing } : null
}

/**
 * What stops a media from entering the playlist as a new row. A file whose
 * length no asset can say cannot be added at all.
 */
function mediaBlock (entry, screens) {
  if (hasAssets(entry.mediaType) && !entry.durationMs) {
    return NO_DURATION
  }

  return orientationBlock(entry, screens)
}

/**
 * The screens a playlist plays on.
 *
 * The server says which scenes a playlist is attached to. An empty list is a
 * playlist on no scene, and nothing about an orientation can be said then: the
 * server checks that the media exists and no more. While the server has not
 * said, and while none of the scenes it named says an orientation, the scene of
 * the page is the only one that can be checked.
 *
 * @param {Array} playlistScenes - The scenes of the playlist, as the server sends them
 * @param {object} routeScene - The scene of the page, as a fallback
 * @returns {Array} `{ name, orientation }` for each screen
 */
function screensOf (playlistScenes, routeScene) {
  if (playlistScenes && playlistScenes.length === 0) {
    return []
  }

  const named = withOrientation(playlistScenes || [])

  return named.length > 0 ? named : withOrientation([routeScene])
}

const withOrientation = scenes => scenes
  .filter(scene => scene && scene.orientation)
  .map(({ name, orientation }) => ({ name: name || null, orientation }))

/**
 * What stops a row of the playlist.
 *
 * A row with a length of its own is never stopped for its length. The server
 * accepts any positive duration, and the operator may have typed that length on
 * the scene page. A length the catalogue cannot give only stops a new row.
 */
function rowBlock (durationMs, media, screens) {
  if (durationMs <= 0) {
    return NO_DURATION
  }

  // The row has a length of its own, so the length the catalogue does not know
  // says nothing about it. The orientation still has to hold, and it is checked
  // here and not read off the media: a media the catalogue could not measure
  // never got an orientation verdict.
  return orientationBlock(media, screens)
}

/**
 * The colour of every label, by its name. A kind is a label, so a kind is
 * looked up here instead of scanned for on every read of a group or a share.
 *
 * @param {Array} labels - The media labels of the app
 * @returns {Map} The name of a label to its colour
 */
const colourByName = labels =>
  new Map((labels || []).map(label => [label.name, label.color || NO_KIND_COLOUR]))

/** The colour of a kind is the colour of the label that is the kind. */
function colourOf (kind, colours) {
  return (kind && colours.get(kind)) || NO_KIND_COLOUR
}

/**
 * This function describes one media, in the shape that both a row and a
 * candidate line use. The kind, the colour and the blocker are derived once
 * here, because a digest reads them for every row and for every cell of the
 * time strip.
 */
function describeMedia (entry, { screens, sceneNames, colours }) {
  const mediaLabels = entry.labels || []
  const kind = kindOf(mediaLabels, { sceneNames })

  return {
    mediaType: entry.mediaType,
    mediaId: entry.mediaId,
    name: entry.name || UNKNOWN_NAME,
    labels: mediaLabels,
    durationMs: entry.durationMs || null,
    // The row of a stored item is checked again, so the value has to stay.
    orientations: entry.orientations || null,
    thumbnailUrl: entry.thumbnailUrl || null,
    createdAt: entry.createdAt || null,
    // The candidate list sorts by this on every keystroke, so the date is read
    // once here and compared as a number.
    createdAtMs: entry.createdAt ? new Date(entry.createdAt).getTime() : 0,
    kind,
    colour: colourOf(kind, colours),
    ...blockedBy(mediaBlock(entry, screens))
  }
}

/**
 * This function describes the media of a stored row that the catalogue does not
 * hold. The row is already in the playlist, so the orientation is not checked.
 * The operator cannot correct it here. A warning on a row that plays today is
 * wrong.
 */
function unknownMedia (item, context) {
  const media = item.media || {}

  return describeMedia({
    mediaType: item.mediaType,
    mediaId: item.mediaId,
    name: media.name || UNKNOWN_NAME,
    labels: media.labels || [],
    durationMs: item.duration,
    orientations: 'both'
  }, context)
}

class Catalogue {
  constructor () {
    this.reset()
  }

  // ===== Commands =====

  /**
   * Forget everything. Another playlist is being opened, and the labels and the
   * scene names of the one before it must not carry over: `setSources` keeps
   * what it has when it is given an empty list, so a full start needs this.
   */
  reset () {
    this._media = []
    this._byLineKey = new Map()
    this._refsByLabel = new Map()
    this._context = { screens: [], sceneNames: [], labels: [], colours: new Map() }
  }

  /** The scenes the playlist plays on. Every media is judged against them. */
  setScreens (screens) {
    this._context = { ...this._context, screens: screens || [] }
  }

  /**
   * Replace the media, the labels and the scene names, and describe every media
   * once. An empty list of labels or of scene names is a list the app has not
   * filled yet, so the one the session has stays.
   */
  setSources (entries, labels, scenes) {
    const kept = labels && labels.length > 0 ? labels : this._context.labels

    this._context = {
      ...this._context,
      labels: kept,
      colours: colourByName(kept),
      sceneNames: hasSceneNames(scenes) ? sceneNamesOf(scenes) : this._context.sceneNames
    }
    this._media = (entries || []).map(entry => describeMedia(entry, this._context))
    // The two maps are built here and not on every read. A right-click reads one
    // line key, and the recipe holds one line per media of the library. The
    // chips are read again after every command of the page.
    this._byLineKey = new Map(this._media.map(media => [mediaKey(media), media]))
    this._refsByLabel = refsByLabel(this._media)
  }

  // ===== Queries =====

  /** The context a caller needs to describe a media of its own. */
  get context () {
    return this._context
  }

  get screens () {
    return this._context.screens
  }

  /** The colour of every label, by name, for a caller that draws a kind. */
  get colours () {
    return this._context.colours
  }

  /** The map from a media reference to the description of that media. */
  byRef () {
    return new Map(this._media.map(media => [mediaRef(media), media]))
  }

  /** The description of one media, or undefined when the library has none. */
  mediaByRef (ref) {
    return this._media.find(media => mediaRef(media) === ref)
  }

  /** The description of the media of one recipe line, by the key of that line. */
  mediaByLineKey (key) {
    return this._byLineKey.get(key)
  }

  /**
   * The label chips. A chip that is on adds its media as candidates.
   *
   * The count is what the click adds, so a media the list already holds is not
   * in it. The caller knows the list and gives its media. A label with no media
   * left shows a zero, and the operator can then delete it in the library.
   *
   * @param {Set} labelFilter - The ids of the chips that are on
   * @param {Set} usedRefs - The media references the list already holds
   */
  labelChips (labelFilter, usedRefs = new Set()) {
    return orderLabels(this._context.labels, this._context.sceneNames)
      .map(label => ({
        id: label.id,
        name: label.name,
        colour: label.color || NO_KIND_COLOUR,
        count: this._newMediaCount(label.id, usedRefs),
        on: labelFilter.has(label.id)
      }))
  }

  /** How many media of one label are not in the list yet. */
  _newMediaCount (labelId, usedRefs) {
    const refs = this._refsByLabel.get(labelId)

    if (!refs) {
      return 0
    }

    return [...refs].filter(ref => !usedRefs.has(ref)).length
  }

  /**
   * The media offered as candidates, newest upload first: the whole library
   * when All media is on, else the media of the label chips.
   */
  candidates ({ allMedia, labelFilter }) {
    if (!allMedia && labelFilter.size === 0) {
      return []
    }

    return this._media
      .filter(media => allMedia || media.labels.some(label => labelFilter.has(label.id)))
      .sort(newestFirst)
  }

  /**
   * The media a takeover can use as its bumper. An act bumper has the Friends
   * kind in the production data, so those come first. A media the scene cannot
   * play is left out, because the copy would not save.
   *
   * The screens are a parameter, because a copy goes to one scene and that need
   * not be a scene of the list the copy is made from.
   */
  bumperChoices (search, screens = this._context.screens, limit = BUMPER_CHOICES) {
    const matches = nameMatcher(search)

    return this._media
      .filter(media => !mediaBlock(media, screens) && matches(media))
      .sort((a, b) => (b.kind === BUMPER_KIND) - (a.kind === BUMPER_KIND) ||
        compareNames(a.name, b.name))
      .slice(0, limit)
  }

  /**
   * The distinct media of a list of rows, by name. The takeover panel offers
   * them for the swap that keeps the length of a list.
   */
  rowMedia (rows) {
    const byRef = new Map()

    rows.forEach(row => {
      const ref = mediaRef(row)
      const seen = byRef.get(ref)

      byRef.set(ref, seen
        ? { ...seen, count: seen.count + 1 }
        : { ref, mediaType: row.mediaType, mediaId: row.mediaId, name: row.media.name, count: 1 })
    })

    return [...byRef.values()].sort((a, b) => compareNames(a.name, b.name))
  }
}

export {
  Catalogue,
  screensOf,
  describeMedia,
  unknownMedia,
  colourOf,
  rowBlock,
  blockedBy,
  mediaRef,
  mediaKey,
  compareNames,
  nameMatcher,
  BLOCKER_SENTENCE,
  NO_KIND_COLOUR,
  NO_KIND_NAME,
  UNKNOWN_NAME
}
